import { PlusIcon } from '@heroicons/react/24/outline';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import InfoTooltip from '@/components/ui/custom/dataDisplay/InfoTooltip';
import { Badge, Button, Divider, TextInput } from '@/components/ui/daisy';
import { MasterSlaveEntry } from '@/data/hook/evaluation/useMasterSlaveMap';
import { ListCacheManager } from '@/data/hook/query/v2/CacheFacade';
import { Assessment } from '@/models/Assessment';
import Evaluation from '@/models/Evaluation';
import { adaptAssessmentsLessonBlocks } from '@/utils/evaluation/adaptAssessmentsLessonBlocks';
import AssessmentItemsSection from './AssessmentItemsSection';
import LessonBlockSelectField from './LessonBlockSelectField';
import Fieldset from '@/components/ui/daisy/fieldset';

export type SlaveLessonBlocksRow = {
  slaveEvaluationId: number;
  lessonBlockIds: number[];
};

export type AssessmentFormFields = {
  name: string;
  description: string;
  maxGrade: number;
  lessonBlocksBySlave: SlaveLessonBlocksRow[];
};

type AssessmentFormProps = {
  evaluation: Evaluation;
  assessment?: Assessment;
  cacheManager: ListCacheManager<Evaluation>;
  slaveEntries: MasterSlaveEntry[];
  onSubmit: (data: AssessmentFormFields) => void;
  onCancel: () => void;
  isLoading?: boolean;
};

export default function AssessmentForm({
  evaluation,
  assessment,
  cacheManager,
  slaveEntries,
  onSubmit,
  onCancel,
  isLoading,
}: AssessmentFormProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.evaluationsTab',
  });

  const defaultValues = useMemo(
    () => ({
      name: assessment?.name ?? '',
      description: assessment?.description ?? '',
      maxGrade: assessment?.maxGrade,
      lessonBlocksBySlave: adaptAssessmentsLessonBlocks(
        slaveEntries,
        assessment?.order,
      ),
    }),
    [assessment, slaveEntries],
  );

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<AssessmentFormFields>({ defaultValues });

  useEffect(() => {
    if (!isLoading) reset(defaultValues);
  }, [defaultValues, reset, isLoading]);

  return (
    <form className="flex flex-col gap-6" onSubmit={handleSubmit(onSubmit)}>
      <TextInput.Fieldset
        label={t('assessmentName')}
        labelAlt={errors?.name?.message}
      >
        <TextInput
          type="text"
          placeholder={t('assessmentNamePlaceholder')}
          className="w-full"
          color={!errors?.name?.message ? 'default' : 'error'}
          {...register('name', { required: t('assessmentNameRequired') })}
        />
      </TextInput.Fieldset>

      <TextInput.Fieldset label={t('assessmentDescription')}>
        <TextInput
          type="text"
          placeholder={t('assessmentDescriptionPlaceholder')}
          className="w-full"
          {...register('description')}
        />
      </TextInput.Fieldset>

      <TextInput.Fieldset
        label={t('assessmentMaxGrade')}
        labelAlt={errors?.maxGrade?.message}
      >
        <TextInput.Wrapper
          color={!errors?.maxGrade?.message ? 'default' : 'error'}
          className="w-full"
        >
          <TextInput
            type="number"
            placeholder={t('assessmentMaxGradePlaceholder')}
            {...register('maxGrade', {
              required: t('assessmentMaxGradeRequired'),
              valueAsNumber: true,
              min: {
                value: 0,
                message: t('assessmentMaxGradePercentage'),
              },
              max: {
                value: 100,
                message: t('assessmentMaxGradePercentage'),
              },
            })}
          />
          <TextInput.Addon>
            <Badge size="xs" variant="soft">
              {t('assessmentMaxGradeMax')}
            </Badge>
          </TextInput.Addon>
        </TextInput.Wrapper>
      </TextInput.Fieldset>

      <ConditionalRenderer condition={slaveEntries.length > 0}>
        <Fieldset label={t('pedagogicalContentSection')}>
          {slaveEntries.map((entry, index) => (
            <LessonBlockSelectField
              key={entry.slave.id}
              index={index}
              coursePathSlug={entry.coursePath.slug}
              coursePathLabel={entry.coursePath.course.abbreviation}
              control={control}
            />
          ))}
        </Fieldset>
      </ConditionalRenderer>

      <Divider className="my-0" />

      <Fieldset label={t('assessmentItemSection')}>
        <InfoTooltip
          condition={!!assessment}
          text={t('addAssessmentItemDisabledTooltip')}
          classNames={{ base: 'self-start' }}
        >
          {assessment ? (
            <AssessmentItemsSection
              evaluation={evaluation}
              assessment={assessment}
              cacheManager={cacheManager}
            />
          ) : (
            <Button
              type="button"
              size="sm"
              variant="outline"
              color="primary"
              disabled
            >
              <PlusIcon className="w-3 h-3" />
              {t('addAssessmentItem')}
            </Button>
          )}
        </InfoTooltip>
      </Fieldset>

      <div className="flex justify-end gap-2">
        <Button
          type="button"
          className="grow bg-base-300"
          onClick={onCancel}
          disabled={isLoading}
        >
          {t('cancel')}
        </Button>
        <Button
          type="submit"
          color="secondary"
          className="grow"
          disabled={isLoading || !isDirty}
          loading={isLoading}
        >
          {t('save')}
        </Button>
      </div>
    </form>
  );
}
